import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle, ArrowLeft } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Forgot password form (no token)
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset password form (with token)
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post("/auth/forgot-password", { email });
      setEmailSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await API.post("/auth/reset-password", { token, password });
      setResetDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold font-[Chivo] tracking-tight">Facilities Management Platform</h1>
          <p className="text-sm text-muted-foreground mt-1">Property Project Management</p>
        </div>

        <div className="border border-border rounded-lg p-6 bg-card shadow-sm">
          {/* No token = Forgot Password form */}
          {!token && !emailSent && (
            <>
              <h2 className="text-lg font-semibold font-[Chivo] mb-1">Forgot Password</h2>
              <p className="text-sm text-muted-foreground mb-6">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label>Email Address</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="your@email.com" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
            </>
          )}

          {/* Email sent confirmation */}
          {!token && emailSent && (
            <div className="text-center py-4" data-testid="reset-email-sent">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={24} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold font-[Chivo] mb-2">Check Your Email</h2>
              <p className="text-sm text-muted-foreground mb-4">If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a password reset link. The link expires in 1 hour.</p>
              <p className="text-xs text-muted-foreground">Didn't receive it? Check your spam folder or <button className="text-primary underline" onClick={() => setEmailSent(false)}>try again</button>.</p>
            </div>
          )}

          {/* Token present = Reset Password form */}
          {token && !resetDone && (
            <>
              <h2 className="text-lg font-semibold font-[Chivo] mb-1">Set New Password</h2>
              <p className="text-sm text-muted-foreground mb-6">Enter your new password below.</p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label>New Password</Label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} placeholder="Min. 8 characters" data-testid="new-password-input" />
                </div>
                <div>
                  <Label>Confirm Password</Label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required placeholder="Confirm your password" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}

          {/* Reset success */}
          {token && resetDone && (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={24} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold font-[Chivo] mb-2">Password Reset</h2>
              <p className="text-sm text-muted-foreground mb-4">Your password has been successfully updated.</p>
              <Link to="/login">
                <Button className="w-full" data-testid="back-to-login-btn">Back to Login</Button>
              </Link>
            </div>
          )}

          {/* Back to login link */}
          {!resetDone && (
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="back-to-login-link">
                <ArrowLeft size={12} /> Back to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
